import {
  QuestionnaireScore,
  MobilityQuestionnaire,
  QuestionnaireStatus,
} from "src/app/core/models/mobility-questionnaire";

export interface QuestionDto {
  questionId: number;
  question: string;
  answerType: "text" | "radio" | "multichoice";
  answer?: string;
  answerChoices?: string[];
}

export interface SectionDto {
  sectionId: number;
  title: string;
  questions: QuestionDto[];
}

export interface QuestionnaireDtoResponse {
  id: string | null;
  createdAt?: string | null;
  completedAt?: string | null;
  updatedAt?: string | null;
  state?: number | string | null;
  sections: SectionDto[];
  sharePointUpdatedForm?: SectionDto[] | null;
  maturityLevel?: "Low" | "Medium" | "High" | null;
  score?: QuestionnaireScore | null;
}

export interface UserQuestionnaireResultDto {
  state: number | string;
  form: QuestionnaireDtoResponse;
  sharePointUpdatedForm?: QuestionnaireDtoResponse | null;
}

export class QuestionnaireResponseMapper {
  private static mapStateFromValue(
    state: number | string | null | undefined
  ): QuestionnaireStatus {
    if (state === null || state === undefined || state === "") {
      return QuestionnaireStatus.New;
    }

    if (typeof state === "number") {
      return state as QuestionnaireStatus;
    }

    const stateMap: Record<string, QuestionnaireStatus> = {
      New: QuestionnaireStatus.New,
      Current: QuestionnaireStatus.Current,
      Obsolete: QuestionnaireStatus.Obsolete,
    };
    return stateMap[state] ?? QuestionnaireStatus.New;
  }

  private static mapScoreFromValue(score: QuestionnaireScore | null | undefined): QuestionnaireScore | null {
    if (score === null || score === undefined)  {
      return null;
    }

    return score;
  }

  static mapWrappedResponse(
    response: UserQuestionnaireResultDto
  ): MobilityQuestionnaire {
    if (!response?.form) {
      return new MobilityQuestionnaire({ id: null, sections: [] });
    }

    const questionnaire = this.mapQuestionnaireDtoToModel(response.form);
    questionnaire.state = this.mapStateFromValue(response.state);
    questionnaire.score = this.mapScoreFromValue(response.form.score);

    if (response.sharePointUpdatedForm?.sections) {
      questionnaire.sharePointUpdatedForm =
        response.sharePointUpdatedForm.sections;
    }

    return questionnaire;
  }

  static mapDirectResponse(
    response: QuestionnaireDtoResponse
  ): MobilityQuestionnaire {
    if (!response) {
      return new MobilityQuestionnaire({ id: null, sections: [] });
    }

    return this.mapQuestionnaireDtoToModel(response);
  }

  private static mapQuestionnaireDtoToModel(
    dto: QuestionnaireDtoResponse
  ): MobilityQuestionnaire {
    if (!dto) {
      return new MobilityQuestionnaire({ id: null, sections: [] });
    }

    return new MobilityQuestionnaire({
      id: dto.id ?? null,
      createdAt: dto.createdAt ? new Date(dto.createdAt) : null,
      completedAt: dto.completedAt ? new Date(dto.completedAt) : null,
      updatedAt: dto.updatedAt ? new Date(dto.updatedAt) : null,
      state: this.mapStateFromValue(dto.state),
      sections: Array.isArray(dto.sections) ? dto.sections : [],
      sharePointUpdatedForm: dto.sharePointUpdatedForm ?? null,
    });
  }
}
